import { ContainerType, ContentItem, MoveContentToContainerRequest } from '~src/ts/ServerApi/requests/MoveContentToContainerRequest';

import { WebClientDetailFetcher } from '../../commonInfra/utils/resourceFetchers/WebClientDetailFetcher';
import { WebClientListResourceFetcher } from '../../commonInfra/utils/resourceFetchers/WebClientListResourceFetcher';
import { IEmptyResult, IMultipleContentTypeResult,IResult } from '../../httpClient/serverApi/types/IResponse';
import { RequestHelper } from '../../httpClient/utils/RequestHelper';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { WebHttpClientWithErrorToastInstance } from '../../httpClient/WebHttpClientWithErrorToast';
import { Sanitizer } from '../../ts/helpers/Sanitizer';
import { ServerApiMapper } from '../../ts/helpers/ServerModelHelper';
import { ProjectMapper } from '../../ts/mappers/ProjectMapper';
import { IProjectDetailInfo, IProjectInfo } from '../../ts/models/IProjectInfo';
import { INamedItemInfo } from '../../ts/models/resourceInfoParts';
import { FieldValuesQuery } from '../../ts/resources/FieldValuesQuery';
import { IActionResource, IDetailResource, IFieldValuesResource, IListResource } from '../../ts/resources/Resource';
import { ResourceQuery } from '../../ts/resources/ResourceQuery';
import { CreateProjectRequest, IProjectIdResult } from '../../ts/ServerApi/requests/CreateProjectRequest';
import { DeleteProjectsRequest } from '../../ts/ServerApi/requests/DeleteProjectsRequest';
import { GetFieldValuesRequest } from '../../ts/ServerApi/requests/GetFieldValuesRequest';
import { GetProjectActionsRequest } from '../../ts/ServerApi/requests/GetProjectActionsRequest';
import {
  GetProjectAncestorsRequest,
  IGetProjectAncestorsResult
} from '../../ts/ServerApi/requests/GetProjectAncestorsRequest';
import { GetProjectsRequest } from '../../ts/ServerApi/requests/GetProjectsRequest';
import { MoveProjectsToProjectRequest } from '../../ts/ServerApi/requests/MoveProjectsToProjectRequest';
import {
  ISetContentControlledPermissionsParams,
  SetContentControlledPermissionsRequest
} from '../../ts/ServerApi/requests/SetContentControlledPermissionsRequest';
import {
  ISetProjectControlledPermissionsParams,
  SetProjectControlledPermissionsRequest
} from '../../ts/ServerApi/requests/SetProjectControlledPermissionsRequest';
import { SetProjectDescriptionRequest } from '../../ts/ServerApi/requests/SetProjectDescriptionRequest';
import { SetProjectNameRequest } from '../../ts/ServerApi/requests/SetProjectNameRequest';
import { SetProjectsOwnerRequest } from '../../ts/ServerApi/requests/SetProjectsOwnerRequest';
import { IActionResult } from '../../ts/ServerApi/types/IActionResult';
import { IProjectActions } from '../../ts/ServerApi/types/IProjectActions';

export class ProjectsResource implements IListResource<IProjectInfo>, IFieldValuesResource, IDetailResource<IProjectDetailInfo>,
    IActionResource<IProjectActions> {
  private listFetcher = new WebClientListResourceFetcher(GetProjectsRequest, ServerApiMapper.projects);
  private detailFetcher = new WebClientDetailFetcher('getProject', ProjectMapper.mapDetail);
  private fieldValuesFetcher = new WebClientListResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);

  public all(): ResourceQuery<IProjectInfo> {
    return new ResourceQuery(this.listFetcher);
  }

  public getById(id: string): Promise<IProjectDetailInfo> {
    return this.detailFetcher.fetchById(id);
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'project');
  }

  // Actions

  public actions(id: string): Promise<IProjectActions> {
    return WebHttpClient.sendRequest(new GetProjectActionsRequest(id));
  }

  public del(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance
      .sendRequestsAndDelay(
        RequestHelper.buildRequests(ids, (idsBlock) => new DeleteProjectsRequest(idsBlock)),
        ResultHelper.combineResults
      );
  }

  public rename(id: string, name: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance
      .sendRequestAndDelay(new SetProjectNameRequest(id, name));
  }

  public setDescription(id: string, description: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance
      .sendRequestAndDelay(new SetProjectDescriptionRequest(id, description));
  }

  public setProjectControlledPermissions(params: ISetProjectControlledPermissionsParams): Promise<IEmptyResult> {
    return WebHttpClientWithErrorToastInstance
      .sendRequest(new SetProjectControlledPermissionsRequest(params));
  }

  public setContentControlledPermissions(params: ISetContentControlledPermissionsParams): Promise<IActionResult> {
    return WebHttpClientWithErrorToastInstance
      .sendRequest(new SetContentControlledPermissionsRequest(params));
  }

  public setOwner(ids: string[], ownerId: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance
      .sendRequestsAndDelay(
        RequestHelper.buildRequests(ids, (idsBlock) => new SetProjectsOwnerRequest(idsBlock, ownerId)),
        ResultHelper.combineResults
      );
  }

  /**
   * Returns all the ancestors in the nested project chain from the top level,
   * including the project with the given ID.
   */
  public getProjectHierarchy(id: string): Promise<INamedItemInfo[]> {
    return WebHttpClientWithErrorToastInstance
      .sendRequest(new GetProjectAncestorsRequest(id))
      .then((result: IGetProjectAncestorsResult) => result.projects ? result.projects : [])
      .catch(_ => []);
  }

  public moveToProject(targetProjectId: string, projectIds: string[]): Promise<IResult> {
    const requests = RequestHelper.buildRequests(
      projectIds,
      idsBlock => new MoveProjectsToProjectRequest({ ids: idsBlock, projectId: targetProjectId })
    );
    return WebHttpClientWithErrorToastInstance
      .sendRequestsAndDelay(requests, ResultHelper.combineResults);
  }

  public create(name: string, description?: string, parentProjectId?: string): Promise<IProjectIdResult> {
    const request = new CreateProjectRequest({
      name: name,
      description: Sanitizer.trimTextOrUndefined(description),
      parentProjectId: parentProjectId
    });
    return WebHttpClientWithErrorToastInstance
      .sendRequestAndDelay(request);
  }

  public moveToContainer(containerId: string, containerType: string, contentItems: ContentItem[]): Promise<IMultipleContentTypeResult> {

    const requests = RequestHelper.buildRequests(
      contentItems,
      contentItemsBlock => new MoveContentToContainerRequest({
        containerId: containerId,
        containerType: ContainerType[containerType as keyof typeof ContainerType],
        contentItems: contentItemsBlock})
    );
    return WebHttpClientWithErrorToastInstance
      .sendRequestsAndDelay(requests, ResultHelper.combineMultipleContentTypeResults);
  }
}

export const Projects = new ProjectsResource();
